// ST-00218 P3: "Location probably wrong" — the tickets whose dispatch and receipt disagree on a
// dispatch / receipt location, so the load lands on the wrong route (or none), with the value to
// set, the evidence and the confidence (services/loadMatching/locationSuggestions on the server).
// Shared by the Load matching drawer (Data quality tab) and the Route Summary indicator.
//
// Order: a location to add to a route's list first (it moves every load it covers, without an
// amendment; the tickets it explains sit under it), then each ticket to correct as the server
// sorted them (high → low). Every suggestion is shown only: the link pre-fills Amendments, where
// the normal amendment and approval rules apply. An ambiguous load lists its candidates and links
// nothing. Bulk: the server's same-fix groups, and a selection of tickets with the same fix.
import { useMemo, useState, type ReactElement } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaQuestionCircle } from 'react-icons/fa';
import { formatMiningDate, formatMiningTime } from '../../../../utils/dateUtils';
import type {
  GroupingLocationSuggestion,
  LocationField,
  LocationSuggestionsPanel,
  RouteName,
  SuggestionBasis,
  SuggestionConfidence,
  TicketLocationSuggestion,
  TicketRef,
  TicketType,
} from '../../../../types/loadMatching';
import { fmtN, fmtT } from './format';
import { TabLink } from './parts';

/** Settings → Route Groupings (where a route's location lists are kept). */
export const ROUTE_GROUPINGS_PATH = '/settings/organization/account-setup/route-groupings';

/** The Amendments hand-off takes at most this many tickets per link (as the server's bulk links). */
const SELECTION_MAX = 100;

const FIELD_WORDS: Record<LocationField, string> = {
  dispatch_location: 'dispatch location',
  receipt_location: 'receipt location',
};

const BASIS_TEXT: Record<SuggestionBasis, string> = {
  delivery_no: 'by delivery no',
  truck_time: 'by truck + time',
  candidate_delivery_no: 'candidate dispatch (same delivery no)',
  candidate_truck_time: 'candidate dispatch (truck + time)',
};

const CONFIDENCE: Record<
  SuggestionConfidence,
  { label: string; title: string; className: string; Icon: typeof FaCheckCircle }
> = {
  high: {
    label: 'High',
    title: 'High confidence: the delivery no and the registration agree with the other ticket',
    className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Icon: FaCheckCircle,
  },
  medium: {
    label: 'Medium',
    title:
      'Medium confidence: matched by truck and time, or the truck’s usual locations decided it — check before you amend',
    className: 'bg-amber-50 text-amber-800 border-amber-200',
    Icon: FaExclamationCircle,
  },
  low: {
    label: 'Low',
    title: 'Low confidence: more than one value fits — check the other ticket and choose',
    className: 'bg-slate-100 text-slate-700 border-slate-300',
    Icon: FaQuestionCircle,
  },
};

const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const plural = (n: number, one: string, many: string): string =>
  `${fmtN(n)} ${n === 1 ? one : many}`;
const ticketTime = (iso: string): string => `${formatMiningDate(iso)} ${formatMiningTime(iso)}`;
const routeNames = (rs: RouteName[]): string =>
  rs.length ? rs.map((r) => r.name).join(', ') : 'No route';
const typeWords = (t: TicketType, n: number): string =>
  t === 'Dispatch' ? (n === 1 ? 'dispatch' : 'dispatches') : n === 1 ? 'receipt' : 'receipts';
const fixText = (field: LocationField, value: string): string =>
  `set the ${FIELD_WORDS[field]} to ${value}`;

/** The hand-off link for several tickets (the same as the server's bulk links). */
export function bulkAmendHref(
  uids: string[],
  field: LocationField,
  ticketType: TicketType,
  value: string
): string {
  const why = `${uids.length} ${typeWords(ticketType, uids.length)} whose load was weighed at “${value}” (load matching, Location probably wrong)`;
  return (
    `/amendments-allocate-transactions?ticketUIDs=${uids.map(encodeURIComponent).join(',')}` +
    `&field=${field}&value=${encodeURIComponent(value)}&why=${encodeURIComponent(why)}`
  );
}

function ConfidenceBadge({ confidence }: { confidence: SuggestionConfidence }) {
  const c = CONFIDENCE[confidence];
  return (
    <span
      data-testid="confidence"
      title={c.title}
      className={`inline-flex items-center gap-1 px-1.5 py-px rounded border text-[10px] font-semibold whitespace-nowrap ${c.className}`}
    >
      <c.Icon className="w-2.5 h-2.5 flex-shrink-0" aria-hidden="true" />
      {c.label}
      <span className="sr-only"> confidence</span>
    </span>
  );
}

function TicketHead({ t }: { t: TicketRef }) {
  return (
    <span className="text-xs text-slate-700 break-words">
      <span className="text-slate-500">{t.ticketType} </span>
      <span className="font-mono font-medium text-slate-900">{t.ticketNo || '(no ticket no)'}</span>
      <span className="text-slate-400"> · </span>
      {ticketTime(t.timestamp)}
      <span className="text-slate-400"> · </span>
      <span className="font-mono">{t.regNo || '(no reg)'}</span>
      <span className="text-slate-400"> · </span>
      {fmtT(t.nettWeight)}
    </span>
  );
}

function TicketSuggestion({
  s,
  canAmend,
  selected,
  onToggle,
}: {
  s: TicketLocationSuggestion;
  canAmend: boolean;
  selected: boolean;
  /** null: not selectable (ambiguous, or no Amendments access). */
  onToggle: (() => void) | null;
}) {
  const p = s.partner;
  const value = s.suggestedValue;
  const selectId = `loc-select-${s.id}`;
  return (
    <li data-testid="location-suggestion" className="py-2 flex items-start gap-2">
      {onToggle && (
        <input
          id={selectId}
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${s.ticket.ticketNo || s.ticket.ticketUid}`}
          className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
        />
      )}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <ConfidenceBadge confidence={s.confidence} />
          <TicketHead t={s.ticket} />
        </div>
        <p className="text-xs text-slate-900 break-words">
          <span className="font-semibold">{capitalise(FIELD_WORDS[s.field])}:</span>{' '}
          <span className="font-mono">{s.currentValue ?? '—'}</span> →{' '}
          <span className="font-mono font-semibold">{value ?? '?'}</span>
        </p>
        {!value && s.candidates.length > 0 && (
          <p className="text-xs text-amber-800 break-words">
            Could be {s.candidates.join(' or ')} — check the other ticket and choose.
          </p>
        )}
        <p className="text-[11px] text-slate-600 break-words">
          <span className="text-slate-500">Evidence: </span>
          {p.ticketType} <span className="font-mono">{p.ticketNo || p.ticketUid}</span> ·{' '}
          {ticketTime(p.timestamp)} · {p.dispatchLocation ?? '—'} → {p.receiptLocation ?? '—'} ·{' '}
          {BASIS_TEXT[s.basis]}
          {s.truckTrips > 0 &&
            ` · the truck ran the corrected pair ${plural(s.truckTrips, 'time', 'times')}`}
        </p>
        <p className="text-[11px] text-slate-600 break-words">
          <span className="text-slate-500">Route: </span>
          {routeNames(s.routesNow)} → {routeNames(s.routesAfter)}
        </p>
        <p className="text-[11px] text-slate-500 break-words">{s.reason}</p>
        {s.amend && value && (
          <div className="pt-0.5">
            {canAmend ? (
              <TabLink href={s.amend.href} srHint="opens Amendments">
                Set the {FIELD_WORDS[s.field]} to {value}
              </TabLink>
            ) : (
              <span className="text-[11px] text-slate-500">
                Set the {FIELD_WORDS[s.field]} to {value}
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function GroupingSuggestion({
  g,
  covered,
  renderTicket,
}: {
  g: GroupingLocationSuggestion;
  covered: TicketLocationSuggestion[];
  renderTicket: (s: TicketLocationSuggestion) => ReactElement;
}) {
  return (
    <li
      data-testid="location-grouping-suggestion"
      className="p-2.5 rounded-md border border-primary-200 bg-primary-50/60 space-y-1"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <ConfidenceBadge confidence={g.confidence} />
        <span className="text-xs font-semibold text-slate-900 break-words">
          Add {g.location} to {g.groupingName}'s {g.side} locations —{' '}
          {plural(g.loads, 'load', 'loads')} of {plural(g.trucks, 'truck', 'trucks')} would move
        </span>
      </div>
      <p className="text-[11px] text-slate-600 break-words">{g.reason}</p>
      {g.examples.length > 0 && (
        <ul aria-label="Example loads" className="space-y-0.5">
          {g.examples.map((t) => (
            <li key={t.ticketUid}>
              <TicketHead t={t} />
            </li>
          ))}
        </ul>
      )}
      <TabLink href={ROUTE_GROUPINGS_PATH} srHint="Settings, Route Groupings">
        Open Route Groupings
      </TabLink>
      {covered.length > 0 && (
        <details className="pt-1">
          <summary className="cursor-pointer select-none text-[11px] font-medium text-slate-700">
            Covered by the grouping suggestion ({fmtN(covered.length)})
          </summary>
          <p className="text-[11px] text-slate-500 mt-1">
            Adding the location to the route moves these without an amendment. Amend them instead
            only if the location on the ticket is really wrong.
          </p>
          <ul className="divide-y divide-slate-100">{covered.map(renderTicket)}</ul>
        </details>
      )}
    </li>
  );
}

export interface LocationSuggestionsListProps {
  panel: LocationSuggestionsPanel;
  /** amendments.view: links into Amendments; without it the suggestions are text only. */
  canAmend: boolean;
  /** Say once that opening Amendments needs the permission (the drawer says it already). */
  showPermissionNote?: boolean;
}

export default function LocationSuggestionsList({
  panel,
  canAmend,
  showPermissionNote = true,
}: LocationSuggestionsListProps) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const items = panel.tickets.items;
  const s = panel.summary;

  // tickets a grouping suggestion explains sit under it (one that is not listed stays loose)
  const { byGrouping, loose } = useMemo(() => {
    const ids = new Set(panel.groupings.map((g) => g.id));
    const m = new Map<string, TicketLocationSuggestion[]>();
    const rest: TicketLocationSuggestion[] = [];
    for (const t of items) {
      if (t.coveredByGrouping && ids.has(t.coveredByGrouping)) {
        const list = m.get(t.coveredByGrouping) ?? [];
        list.push(t);
        m.set(t.coveredByGrouping, list);
      } else rest.push(t);
    }
    return { byGrouping: m, loose: rest };
  }, [items, panel.groupings]);

  const chosen = items.filter((t) => selected.has(t.id));
  const first = chosen[0];
  const sameFix =
    !!first &&
    !!first.suggestedValue &&
    chosen.every(
      (t) =>
        t.field === first.field &&
        t.ticket.ticketType === first.ticket.ticketType &&
        t.suggestedValue === first.suggestedValue
    );
  const selectionUids = [...new Set(chosen.map((t) => t.ticket.ticketUid))];

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderTicket = (t: TicketLocationSuggestion) => (
    <TicketSuggestion
      key={t.id}
      s={t}
      canAmend={canAmend}
      selected={selected.has(t.id)}
      onToggle={canAmend && t.amend && t.suggestedValue ? () => toggle(t.id) : null}
    />
  );

  const empty = items.length === 0 && panel.groupings.length === 0;
  return (
    <div data-testid="location-suggestions" className="space-y-3">
      {empty ? (
        <p className="text-xs text-slate-600 py-2">
          No tickets look like they carry the wrong location in this period.
        </p>
      ) : (
        <div className="space-y-1">
          <p className="text-xs text-slate-700">
            {plural(s.tickets, 'ticket probably carries', 'tickets probably carry')} the wrong
            location: {fmtN(s.high)} high, {fmtN(s.medium)} medium, {fmtN(s.low)} low confidence
            {s.groupings > 0 && ` · ${plural(s.groupings, 'route list', 'route lists')} to check`}
          </p>
          <p className="text-[11px] text-slate-500">
            Each suggestion is shown only. Nothing changes until an amendment is saved and approved;
            the route figures move once it is.
          </p>
          {showPermissionNote && !canAmend && (
            <p className="text-[11px] text-slate-500">
              Opening them in Amendments needs the Amendments permission.
            </p>
          )}
        </div>
      )}

      {panel.groupings.length > 0 && (
        <ul aria-label="Route lists to check" className="space-y-2">
          {panel.groupings.map((g) => (
            <GroupingSuggestion
              key={g.id}
              g={g}
              covered={byGrouping.get(g.id) ?? []}
              renderTicket={renderTicket}
            />
          ))}
        </ul>
      )}

      {panel.bulk.length > 0 && (
        <div data-testid="location-bulk" className="space-y-1">
          <p className="text-[11px] font-semibold text-slate-700">Same fix for several tickets</p>
          <ul className="space-y-1">
            {panel.bulk.map((b) => {
              const n = b.ticketUids.length;
              const text = `Amend ${plural(n, typeWords(b.ticketType, 1), typeWords(b.ticketType, 2))} at once: ${fixText(b.field, b.suggestedValue)}`;
              return (
                <li
                  key={`${b.field}-${b.ticketType}-${b.suggestedValue}`}
                  className="flex flex-wrap gap-x-4 gap-y-1"
                >
                  {!canAmend ? (
                    <span className="text-[11px] text-slate-500">{text}</span>
                  ) : b.hrefs.length === 1 ? (
                    <TabLink href={b.hrefs[0]!} srHint="opens Amendments">
                      {text}
                    </TabLink>
                  ) : (
                    <>
                      <span className="text-[11px] text-slate-700">{text}</span>
                      {b.hrefs.map((h, i) => (
                        <TabLink key={h} href={h} srHint="opens Amendments">
                          Part {i + 1} of {b.hrefs.length}
                        </TabLink>
                      ))}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {chosen.length > 0 && (
        <div
          data-testid="location-selection"
          role="status"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 p-2 rounded-md border border-slate-200 bg-slate-50 text-[11px] text-slate-700"
        >
          <span>{plural(chosen.length, 'ticket', 'tickets')} selected.</span>
          {!sameFix ? (
            <span>Select tickets with the same fix to amend them together.</span>
          ) : selectionUids.length > SELECTION_MAX ? (
            <span>Select at most {SELECTION_MAX} tickets to amend them together.</span>
          ) : (
            <TabLink
              href={bulkAmendHref(
                selectionUids,
                first.field,
                first.ticket.ticketType,
                first.suggestedValue!
              )}
              srHint="opens Amendments"
            >
              Amend the {plural(selectionUids.length, 'selected ticket', 'selected tickets')} at
              once: {fixText(first.field, first.suggestedValue!)}
            </TabLink>
          )}
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto underline text-slate-600 hover:text-slate-900"
          >
            Clear selection
          </button>
        </div>
      )}

      {loose.length > 0 && (
        <ul aria-label="Tickets to correct" className="divide-y divide-slate-100">
          {loose.map(renderTicket)}
        </ul>
      )}
      {panel.tickets.truncated && (
        <p className="text-[11px] text-slate-500">
          Showing {fmtN(items.length)} of {fmtN(panel.tickets.total)}, most certain first.
        </p>
      )}
    </div>
  );
}
